import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/adminService';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Modal, { ModalHeader, ModalContent, ModalFooter } from '../../components/ui/Modal';
import FormInput from '../../components/ui/FormInput';

const AdminPayoutsPage: React.FC = () => {
  const [payouts, setPayouts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayout, setSelectedPayout] = useState<any | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [newStatus, setNewStatus] = useState<'PROCESSING' | 'COMPLETED' | 'FAILED'>('COMPLETED');
  const [failureReason, setFailureReason] = useState('');

  const load = async () => {
    setLoading(true);
    const res = await adminService.listPayouts();
    setPayouts(res.payouts || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleUpdate = (payout: any) => {
    setSelectedPayout(payout);
    setNewStatus(payout.status === 'COMPLETED' ? 'COMPLETED' : 'COMPLETED');
    setFailureReason('');
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!selectedPayout) return;
    await adminService.updatePayoutStatus(selectedPayout.id, newStatus, failureReason || undefined);
    setShowModal(false);
    setSelectedPayout(null);
    await load();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Payout Management</h1>

      <div className="flex items-center gap-2 mb-4">
        <Button variant="outline" onClick={load}>Refresh</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Creator</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Processed</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr><td className="px-4 py-6" colSpan={6}>Loading…</td></tr>
                ) : payouts.length === 0 ? (
                  <tr><td className="px-4 py-6" colSpan={6}>No payouts found.</td></tr>
                ) : payouts.map(p => (
                  <tr key={p.id}>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">
                        {p.creatorProfile?.user?.username || p.creatorProfileId}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{p.currency} {p.amount.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        p.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                        p.status === 'PROCESSING' ? 'bg-yellow-100 text-yellow-700' :
                        p.status === 'FAILED' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{new Date(p.createdAt).toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{p.processedAt ? new Date(p.processedAt).toLocaleString() : '—'}</td>
                    <td className="px-4 py-3">
                      <Button size="sm" variant="outline" onClick={() => handleUpdate(p)}>Update</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
        <ModalHeader>Update Payout Status</ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select value={newStatus} onChange={(e) => setNewStatus(e.target.value as any)} className="w-full px-3 py-2 border rounded">
                <option value="PROCESSING">Processing</option>
                <option value="COMPLETED">Completed</option>
                <option value="FAILED">Failed</option>
              </select>
            </div>
            {newStatus === 'FAILED' && (
              <FormInput label="Failure Reason" value={failureReason} onChange={(e) => setFailureReason(e.target.value)} />
            )}
          </div>
        </ModalContent>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={handleSubmit}>Submit</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default AdminPayoutsPage;

